import { CheckCircle2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import { Button } from '../components/Button.js';
import { NovaOrb } from '../components/NovaOrb.js';
import { ApiError } from '../lib/api.js';

const inputClass =
  'border-hairline bg-base/60 text-ink focus:border-accent/50 h-11 w-full rounded-lg border px-3 text-sm transition';

/**
 * Step two of the forgot-password flow: set a new password with the token from
 * the e-mailed link. Same rules as the change-password dialog — min 10 chars,
 * confirmed — plus a friendly path back when the link is missing or spent.
 */
export function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // No token at all: nothing to do here — send them to ask for a fresh link.
  if (!token) return <Navigate to="/glemt-passord" replace />;

  const mismatch = confirm.length > 0 && next !== confirm;
  const valid = next.length >= 10 && next === confirm;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!valid) return;
    setError(null);
    setBusy(true);
    try {
      await resetPassword(token!, next);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Noe gikk galt. Prøv igjen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <NovaOrb size={18} />
          <h1 className="font-display text-ink mt-4 text-3xl font-bold tracking-[0.3em]">NOVA</h1>
          <p className="text-ink-soft mt-2 text-sm">Velg nytt passord</p>
        </div>

        {done ? (
          <div className="glass shadow-panel space-y-4 p-6">
            <div className="text-ink-soft flex items-start gap-3 text-sm">
              <CheckCircle2 size={18} className="text-success mt-0.5 shrink-0" aria-hidden />
              <p>Passordet er endret. Du kan logge inn med det nye passordet nå.</p>
            </div>
            <Button variant="primary" className="w-full" onClick={() => navigate('/logg-inn')}>
              Til innlogging
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="glass shadow-panel space-y-4 p-6">
            <div>
              <label htmlFor="new-password" className="label-micro mb-1.5 block">
                Nytt passord
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                autoFocus
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className={inputClass}
              />
              <p className="text-ink-soft/70 mt-1 text-xs">Minst 10 tegn.</p>
            </div>

            <div>
              <label htmlFor="confirm-password" className="label-micro mb-1.5 block">
                Bekreft nytt passord
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={inputClass}
              />
              {mismatch && <p className="text-danger mt-1 text-xs">Passordene er ikke like.</p>}
            </div>

            {error && (
              <p
                role="alert"
                className="border-danger/30 bg-danger/10 text-danger rounded-lg border px-3 py-2 text-sm"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              loading={busy}
              disabled={!valid}
              className="w-full"
            >
              Lagre nytt passord
            </Button>
            <Link
              to="/glemt-passord"
              className="text-ink-soft hover:text-ink block text-center text-xs transition"
            >
              Be om ny lenke
            </Link>
          </form>
        )}
      </div>
    </main>
  );
}
